import { normalizeTone } from '../../../../constants/smartleadbot.constants';
import { INTEGRATION_TO_API_MAP } from './SmartLeadbot.constants';
import { AxiosService } from '../../../../axios/axios.service';
import type {
  tSmartLeadbotConfig,
  tApiFunnelStage,
  tChatMessage,
  tSandboxAttachment,
  tSandboxButton,
  tSandboxChatResponse,
  tResumeContext,
  tSandboxStageConfig,
} from './SmartLeadbot.types';
import type { tMessageSource } from 'src/types/messageSources.types';
import type { Document } from 'src/pages/LeadAgentConfig/data/contract/contract.generated';

// ─── BFF response wrappers ────────────────────────────────────────────────────

type tBffResponse<T> = {
  success: boolean;
  code: number;
  message: string;
  data: T;
};

// BFF pipe Stage shape — GET /workspace/:wid/pipe/:pid/stages
type tBffStage = {
  id: string;
  order: number;
  name: string;
  expiresAfter: number;
  sumValue: string;
  description?: string;
  botEnabled?: boolean;
};

// Stage description entry returned by the BFF auto-generate endpoint. Keying by
// stageId lets the FE map descriptions back to the right stage even if the
// funnel reordered or added stages between page load and the generate call.
export type tStageDescription = {
  stageId: string;
  description: string;
};

// ─── Sandbox chat request/response ───────────────────────────────────────────

type tSandboxWorkspaceContext = {
  businessContext?: string;
  knowledgeBase?: string;
  tone?: string;
  botRestrictions?: string[];
  handoffTriggers?: string[];
};

// Wire shape mirroring `leads-ai-service` MessageAttachmentSchema
// (shared.schema.ts:3-7) — snake_case stays at the service boundary so the
// FE keeps camelCase everywhere else.
type tWireAttachment = {
  media_type: 'IMAGE' | 'AUDIO' | 'VIDEO' | 'DOCUMENT';
  url: string;
  mimetype: string;
};

// Wire shape of attachments the AGENT sends back in the sandbox response
// (RET-1213). Same media fields as the SEND shape plus a `name` (rendered by the
// DOCUMENT card). Maps to camelCase `tSandboxAttachment`.
type tWireResponseAttachment = tWireAttachment & {
  name: string;
};

// Raw response shape off the wire — same as tSandboxChatResponse but with the
// snake_case `attachments` the BFF emits. Mapped to camelCase before returning.
// Exported: the async job result (GET job status + the websocket push) carries
// this exact same wire shape, so both the poll and socket paths reuse the
// mapping this service already owns for the synchronous response.
//
// AGI-243: bff-web emits the "Ver fuentes" grounding data under the key
// `resources`, not `sources`. The wire type must say `resources` here so it
// tells the truth about what actually arrives; mapSandboxChatResponse below
// renames it to camelCase-domain `sources`.
//
// The two names diverge on purpose, not by mistake: the chat event payload
// uses `sources`, but the sandbox response is a literal forward of the shape
// chatbot-service emits, and chatbot-service calls the field `resources`.
// Don't "fix" this by renaming either side.
export type tWireSandboxChatResponse = Omit<tSandboxChatResponse, 'attachments' | 'sources'> & {
  attachments?: tWireResponseAttachment[];
  resources?: tMessageSource[];
};

// Async sandbox job (202 + job_id) — the job resource returned by the polling
// GET and, structurally, the payload the websocket push carries.
export type tSandboxChatJobStatus = 'pending' | 'done' | 'failed';

export type tSandboxChatJobResponse = {
  job_id: string;
  status: tSandboxChatJobStatus;
  result?: tWireSandboxChatResponse;
  error?: string;
};

export function mapSandboxChatResponse(wire: tWireSandboxChatResponse): tSandboxChatResponse {
  // Destructure the wire-only keys out of `rest` so the spread below can't
  // leak `resources` (wire naming) into the domain shape — only the mapped
  // `sources` (camelCase-domain naming) is returned.
  const { attachments, resources, ...rest } = wire;
  return {
    ...rest,
    attachments: mapResponseAttachments(attachments),
    sources: resources,
  };
}

function mapResponseAttachments(wire?: tWireResponseAttachment[]): tSandboxAttachment[] | undefined {
  if (!wire || wire.length === 0) return undefined;
  return wire.map((a) => ({
    id: crypto.randomUUID(),
    mediaType: a.media_type,
    url: a.url,
    mimetype: a.mimetype,
    name: a.name,
  }));
}

type tSandboxChatBody = {
  messages: {
    role: 'user' | 'assistant';
    content: string;
    attachments?: tWireAttachment[];
    buttons?: tSandboxButton[];
  }[];
  stageConfig: tSandboxStageConfig;
  agentName?: string;
  resumeContext?: tResumeContext;
  // Top-level flag forwarded to leads-ai-service to skip the handoff evaluator.
  suppressHandoff?: boolean;
  // Accumulated steering instructions forwarded as array to chatbot-service.
  steeringInstructions?: string[];
  // Inline workspace context — WYSIWYG sandbox: what the operator sees in the
  // smart-config form is exactly what the AI gets, no save-before-test required.
  // chatbot-service uses this to skip the workspace-service fetch when present
  // and threads tone/businessContext through to leads-ai-service's dedicated
  // template variables ({{tone}}, business_context layer of workspace_soul).
  workspaceContext?: tSandboxWorkspaceContext;
};

type tSandboxDraftChatBody = Pick<tSandboxChatBody, 'messages' | 'stageConfig'> & { draft: Document; async: true; sessionId?: string; turnId?: string };

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Build the stage config payload for the sandbox chat request.
 * @param config - The full SmartLeadbot config.
 * @param currentStageIndex - Zero-based index of the lead's current stage in the funnel.
 */
function buildStageConfig(config: tSmartLeadbotConfig, currentStageIndex?: number): tSandboxStageConfig {
  return {
    stages: config.apiStages.map((s) => ({
      id: s.id,
      name: s.name,
      isBot: config.stageAssignments[s.id] === 'bot',
      description: s.description || undefined,
      advanceCriteria: config.advanceCriteria[s.id] || undefined,
    })),
    qualificationQuestions: config.qualificationQuestions,
    botTone: config.botTone || undefined,
    botRestrictions: config.botRestrictions.length > 0 ? config.botRestrictions : undefined,
    handoffTriggers: config.handoffTriggers.length > 0 ? config.handoffTriggers : undefined,
    currentStageIndex,
  };
}

// Shared by the sync (chatMessage) and async (submitSandboxChatAsync) sandbox
// chat requests — both send the identical turn payload, differing only in
// whether the request also carries `async: true`.
function buildSandboxChatBody(options: {
  messages: tChatMessage[];
  config: tSmartLeadbotConfig;
  resumeContext?: tResumeContext;
  currentStageIndex?: number;
  steeringInstructions?: string[];
}): tSandboxChatBody {
  const { messages, config, resumeContext, currentStageIndex, steeringInstructions } = options;
  // Populate every field the smart-config form can edit, so the AI receives
  // EXACTLY what the operator sees — full WYSIWYG, no save-before-test friction.
  // Empty values are omitted via spread guards so chatbot-service falls back to
  // workspace-service per-field rather than overriding with empty strings.
  const workspaceContext: tSandboxWorkspaceContext = {
    ...(config.businessContext && { businessContext: config.businessContext }),
    ...(config.knowledgeBase && { knowledgeBase: config.knowledgeBase }),
    ...(config.botTone && { tone: config.botTone }),
    ...(config.botRestrictions.length > 0 && { botRestrictions: config.botRestrictions }),
    ...(config.handoffTriggers.length > 0 && { handoffTriggers: config.handoffTriggers }),
  };
  return {
    messages: messages.map((m) => {
      const role: 'user' | 'assistant' = m.role === 'bot' ? 'assistant' : 'user';
      // Only forward attachments on user-role messages — leads-ai-service's
      // multimodal pipeline expects them upstream of an assistant turn (the
      // assistant's previous reply has no attachments to re-send).
      const wireAttachments: tWireAttachment[] | undefined =
        role === 'user' && m.attachments && m.attachments.length > 0
          ? m.attachments.map((a) => ({
              media_type: a.mediaType,
              url: a.url,
              mimetype: a.mimetype,
            }))
          : undefined;
      return {
        role,
        content: m.text,
        ...(wireAttachments && { attachments: wireAttachments }),
        ...(role === 'assistant' && m.buttons && m.buttons.length > 0 && { buttons: m.buttons }),
      };
    }),
    stageConfig: buildStageConfig(config, currentStageIndex),
    ...(config.agentName && { agentName: config.agentName }),
    ...(resumeContext && { resumeContext }),
    ...(resumeContext?.suppressHandoff && { suppressHandoff: true }),
    ...(steeringInstructions && steeringInstructions.length > 0 && { steeringInstructions }),
    ...(Object.keys(workspaceContext).length > 0 && { workspaceContext }),
  };
}

// ─── Service ──────────────────────────────────────────────────────────────────

// Minimal shape consumed from GET /smartleadbot/config. The endpoint returns
// additional fields (businessContext, tone, activeIntegrations, etc.), but
// onboarding only needs the locked funnelId as a source of truth for the PATCH.
type tExistingSmartLeadbotConfig = {
  funnelId?: string;
};

export const SmartLeadbotService = Object.freeze({
  /**
   * GET /ai/workspaces/:wid/smartleadbot/config
   * Returns the current Lead Agent config. Used by onboarding to read the
   * server's locked funnelId before issuing a PATCH, avoiding a 422 when the
   * frontend state holds a stale or different funnelId than the one persisted
   * during initial setup (see RET-796).
   *
   * Resolves to null only when the config does not yet exist (HTTP 404 →
   * first-time activation). Rethrows for every other failure mode (network,
   * 5xx, timeout, etc.) because falling back to the frontend-held funnelId on
   * those paths can reintroduce the same 422 `FunnelIdLockedError` this layer
   * exists to prevent — an existing config may be present server-side even
   * when the GET transiently fails.
   */
  getConfig: async (workspaceId: string): Promise<tExistingSmartLeadbotConfig | null> => {
    try {
      return await AxiosService.get<tExistingSmartLeadbotConfig>(
        `ai/workspaces/${workspaceId}/smartleadbot/config`,
        { isBFFService: true },
      );
    } catch (error) {
      // Narrow the error to an axios-shaped object so we can read the HTTP
      // status without importing axios directly. Any non-404 failure is
      // unknown-state and must propagate so the caller can abort the save
      // rather than guess a funnelId that might mismatch a server lock.
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * PATCH /ai/workspaces/:wid/smartleadbot/config
   * Partial config update — only supplied fields are modified in chatbot-service.
   */
  saveConfig: async (
    workspaceId: string,
    config: tSmartLeadbotConfig,
    funnelId: string | undefined,
  ): Promise<void> => {
    // Normalize botTone to the canonical API value in case a display label was stored.
    const canonicalTone = config.botTone ? normalizeTone(config.botTone) : config.botTone;
    // Only include activeIntegrations when the user explicitly selected a channel.
    // An empty selectedIntegration (skip path) omits the field so the PATCH does not
    // overwrite any previously configured integrations.
    const activeIntegrationsPayload = INTEGRATION_TO_API_MAP[config.selectedIntegration]
      ? { activeIntegrations: INTEGRATION_TO_API_MAP[config.selectedIntegration] }
      : {};
    await AxiosService.patch<object, unknown>(
      `ai/workspaces/${workspaceId}/smartleadbot/config`,
      {
        businessContext: config.businessContext,
        tone: canonicalTone,
        agentName: config.agentName,
        handoffTriggers: config.handoffTriggers,
        botRestrictions: config.botRestrictions,
        aiRespondEnabled: config.aiRespondEnabled ?? true,
        ...activeIntegrationsPayload,
        ...(funnelId && { funnelId }),
      },
      { isBFFService: true },
    );
  },

  /**
   * GET /workspace/:wid/pipe/:pid/stages
   * Returns funnel stages from the BFF pipe route.
   * The BFF includes botEnabled per stage (sourced from leads-service bot_enabled column).
   */
  getStages: async (workspaceId: string, funnelId: string): Promise<tApiFunnelStage[]> => {
    const result = await AxiosService.get<tBffResponse<{ stages: tBffStage[] }>>(
      `workspace/${workspaceId}/pipe/${funnelId}/stages`,
      { isBFFService: true },
    );
    return (result.data.stages ?? []).map((s) => ({
      id: s.id,
      name: s.name,
      description: s.description ?? '',
      order: s.order,
      botEnabled: s.botEnabled ?? true,
    }));
  },

  /**
   * PUT /workspace/:wid/pipe/:pid/stages/bot-enabled
   * Persists stage bot/human assignments to leads-service via BFF.
   */
  saveBotEnabled: async (
    workspaceId: string,
    funnelId: string,
    assignments: { stageId: string; botEnabled: boolean }[],
  ): Promise<void> => {
    await AxiosService.put<{ assignments: { stageId: string; botEnabled: boolean }[] }, unknown>(
      `workspace/${workspaceId}/pipe/${funnelId}/stages/bot-enabled`,
      { assignments },
      { isBFFService: true },
    );
  },

  /**
   * GET /workspace/:wid/funnel/:fid/stage/:sid/questions
   * Smart endpoint — leads-service returns existing questions or generates via AI if empty.
   */
  getQualificationQuestions: async (
    workspaceId: string,
    funnelId: string,
    stageId: string,
  ): Promise<string[]> => {
    const result = await AxiosService.get<tBffResponse<{ questions: string[] }>>(
      `workspace/${workspaceId}/funnel/${funnelId}/stage/${stageId}/questions`,
      { isBFFService: true },
    );
    return result.data.questions ?? [];
  },

  /**
   * PUT /workspace/:wid/funnel/:fid/stage/:sid/questions
   * Replaces the entire qualification_questions array for a stage.
   */
  saveQualificationQuestions: async (options: {
    workspaceId: string;
    funnelId: string;
    stageId: string;
    questions: string[];
  }): Promise<void> => {
    const { workspaceId, funnelId, stageId, questions } = options;
    await AxiosService.put<{ questions: string[] }, unknown>(
      `workspace/${workspaceId}/funnel/${funnelId}/stage/${stageId}/questions`,
      { questions },
      { isBFFService: true },
    );
  },

  /**
   * PUT /workspace/:wid/funnel/:fid/stage/:sid/advance-criteria
   * Replaces the advance-criteria prose for a stage. An empty string clears it and
   * re-enables the legacy qualification-questions fallback downstream.
   */
  saveAdvanceCriteria: async (options: {
    workspaceId: string;
    funnelId: string;
    stageId: string;
    criterion: string;
  }): Promise<void> => {
    const { workspaceId, funnelId, stageId, criterion } = options;
    // bff-web's SaveAdvanceCriteriaParams declares `Criterion string \`json:"criterion"\``
    // (pipe.params.go) and forwards `{criterion}` to leads-service's
    // ApiAdvanceCriteriaController, which validates `'criterion' => ['nullable','string']`.
    // Sending `advance_criteria` here makes encoding/json silently drop the field at the
    // BFF layer — Save returns 200 but the column persists empty. PR #2237 introduced
    // this mismatch; this restores the contract end-to-end.
    await AxiosService.put<{ criterion: string }, unknown>(
      `workspace/${workspaceId}/funnel/${funnelId}/stage/${stageId}/advance-criteria`,
      { criterion },
      { isBFFService: true },
    );
  },

  /**
   * POST /ai/workspaces/:wid/smartleadbot/sandbox/chat
   * Sends a test message to the SmartLeadbot AI sandbox and returns the full evaluation response.
   */
  chatMessage: async (options: {
    messages: tChatMessage[];
    config: tSmartLeadbotConfig;
    workspaceId: string;
    resumeContext?: tResumeContext;
    currentStageIndex?: number;
    steeringInstructions?: string[];
  }): Promise<tSandboxChatResponse> => {
    const { workspaceId, ...rest } = options;
    const body = buildSandboxChatBody(rest);
    const wire = await AxiosService.post<tSandboxChatBody, tWireSandboxChatResponse>(
      `ai/workspaces/${workspaceId}/smartleadbot/sandbox/chat`,
      body,
      // 90s, not the 30s default. A sandbox turn runs a real agent loop — leads-ai's
      // own LLM budget is 85s with up to 11 tool steps — so the default guaranteed
      // that any turn past 30s "failed" in the browser while the backend finished
      // and answered 200 to nobody. Verified from both ends: axios logged `timeout
      // of 30000ms exceeded` in the same minute bff-web logged the 200. The async
      // job path (VITE_SANDBOX_ASYNC_FEATURE) is the structural fix; this keeps the
      // sync path honest wherever that flag is off.
      { isBFFService: true, timeout: 90_000 },
    );
    // `feedback` and `buttons` ride through the `...wire` spread untouched — the
    // BFF already delivers the final shape (snake_case `reason_es`, camelCase
    // `stageId`/`buttons[].id`/`buttons[].title`), so remapping casing here
    // would diverge from the wire contract. Only `attachments` needs camelCase
    // normalization at this boundary.
    return mapSandboxChatResponse(wire);
  },

  /**
   * POST /ai/workspaces/:wid/smartleadbot/sandbox/chat (async:true)
   * Same turn payload as `chatMessage`, but opts into the async job path
   * (202 + job_id) behind `VITE_SANDBOX_ASYNC_FEATURE`. No auth_id on the
   * wire — bff-web derives it server-side from the validated token and
   * injects it when forwarding to chatbot-service; a client-sent value would
   * never be parsed.
   */
  submitSandboxChatAsync: async (options: {
    messages: tChatMessage[];
    config: tSmartLeadbotConfig;
    workspaceId: string;
    resumeContext?: tResumeContext;
    currentStageIndex?: number;
    steeringInstructions?: string[];
  }): Promise<{ jobId: string }> => {
    const { workspaceId, ...rest } = options;
    const body = buildSandboxChatBody(rest);
    const wire = await AxiosService.post<tSandboxChatBody & { async: boolean }, { job_id: string }>(
      `ai/workspaces/${workspaceId}/smartleadbot/sandbox/chat`,
      { ...body, async: true },
      { isBFFService: true },
    );
    return { jobId: wire.job_id };
  },

  /**
   * POST /ai/workspaces/:wid/smartleadbot/sandbox/chat (async:true) with the whole draft Document.
   * bff-web validates the Document, derives the workspace context itself and applies the draft
   * only to this sandbox turn; the caller sends no agentName or workspaceContext.
   */
  createSandboxSession: async (workspaceId: string): Promise<unknown> =>
    AxiosService.post<Record<string, never>, unknown>(
      `ai/workspaces/${workspaceId}/smartleadbot/sandbox/sessions`,
      {},
      { isBFFService: true, skipSentryCapture: true },
    ),

  readSandboxSession: async (options: { workspaceId: string; sessionId: string; after?: string }): Promise<unknown> =>
    AxiosService.get<unknown>(
      `ai/workspaces/${options.workspaceId}/smartleadbot/sandbox/sessions/${options.sessionId}?limit=50${options.after ? `&after=${options.after}` : ''}`,
      { isBFFService: true, skipSentryCapture: true },
    ),

  submitSandboxDraftChatAsync: async (options: {
    workspaceId: string;
    sessionId?: string;
    turnId?: string;
    messages: tSandboxChatBody['messages'];
    stageConfig: tSandboxStageConfig;
    draft: Document;
  }): Promise<unknown> => {
    const { workspaceId, ...body } = options;
    return AxiosService.post<tSandboxDraftChatBody, unknown>(
      `ai/workspaces/${workspaceId}/smartleadbot/sandbox/chat`,
      { ...body, async: true },
      { isBFFService: true, skipSentryCapture: true },
    );
  },

  /**
   * GET /ai/workspaces/:wid/smartleadbot/sandbox/chat/jobs/:job_id
   * Polls for the async sandbox job's status/result.
   */
  getSandboxChatJobStatus: async (
    workspaceId: string,
    jobId: string,
  ): Promise<tSandboxChatJobResponse> => {
    return AxiosService.get<tSandboxChatJobResponse>(
      `ai/workspaces/${workspaceId}/smartleadbot/sandbox/chat/jobs/${encodeURIComponent(jobId)}`,
      { isBFFService: true },
    );
  },

  /**
   * GET /workspace/:wid/funnel/:fid/recommended-questions
   * Returns AI-generated recommended qualification questions for the funnel.
   */
  getRecommendedQuestions: async (
    workspaceId: string,
    funnelId: string,
  ): Promise<string[]> => {
    const result = await AxiosService.get<tBffResponse<{ questions: string[] }>>(
      `workspace/${workspaceId}/funnel/${funnelId}/recommended-questions`,
      { isBFFService: true },
    );
    return result.data.questions ?? [];
  },

  /**
   * POST /workspace/:wid/funnel/:fid/categorize-questions
   * Categorizes a list of questions into funnel stages via AI.
   */
  categorizeQuestions: async (
    workspaceId: string,
    funnelId: string,
    questions: string[],
    stages: Array<{ id: string; name: string; description: string }>,
  ): Promise<Record<string, string[]>> => {
    if (questions.length === 0) return {};
    const result = await AxiosService.post<
      { questions: string[]; stages: Array<{ id: string; name: string; description: string }>;
      },
      tBffResponse<{ questionsByStage: Array<{ stageId: string; questions: string[] }>;
      }>
    >(
      `workspace/${workspaceId}/funnel/${funnelId}/categorize-questions`,
      { questions, stages },
      { isBFFService: true },
    );
    const map: Record<string, string[]> = {};
    for (const entry of result.data.questionsByStage ?? []) {
      map[entry.stageId] = entry.questions;
    }
    return map;
  },

  /**
   * POST /workspace/:wid/funnel/:fid/stage-descriptions
   * Generates AI-powered stage descriptions for all stages in the funnel.
   * Returns descriptions keyed by stageId so callers can map safely even if
   * stages were reordered or added after the funnel was first fetched.
   *
   * Response shape: AxiosService.post returns `response.data` from axios (see
   * axios.service.ts:125), which is the BFF envelope `{ success, code, data }`.
   * So `result.data.descriptions` is the array — NOT `result.data.data.descriptions`.
   * This matches the pattern in `getStages` above.
   */
  generateStageDescriptions: async (options: {
    workspaceId: string;
    funnelId: string;
    workspaceSoul: string;
  }): Promise<tStageDescription[]> => {
    const { workspaceId, funnelId, workspaceSoul } = options;
    // 45s client-side cap: the axios default is infinite and the BFF caps at 58s. Without
    // an explicit timeout, a stalled BFF would pin `generatingDescriptions=true` forever,
    // block the save mutex, and the user would lose unsaved edits on page refresh.
    const result = await AxiosService.post<
      { workspaceSoul: string },
      tBffResponse<{ descriptions: tStageDescription[] }>
    >(
      `workspace/${workspaceId}/funnel/${funnelId}/stage-descriptions`,
      { workspaceSoul },
      { isBFFService: true, timeout: 45_000 },
    );
    // Defensive: if the envelope is somehow malformed (proxy bug, backend hiccup),
    // prefer an empty array over crashing the caller. The caller's no-match warning
    // will still surface a clear diagnostic.
    return result?.data?.descriptions ?? [];
  },

});
